import React from 'react';

import useLocalStorage from '~/hooks/useLocalStorage';

import {
	type NavigationLayout,
	getNavigationMigrationConfig,
} from '~/config/navigationMigration';


// Use namespaced key to avoid conflicts with other localStorage keys
const STORAGE_KEY = 'navigation/migration/layout';

type NavigationLayoutContextValue = {
	currentLayout: NavigationLayout;
	setLayout: (layout: NavigationLayout) => void;
	showToggle: boolean;
};

const NavigationLayoutContext = React.createContext<NavigationLayoutContextValue | null>(null);

/**
 * Navigation Layout Context Provider
 *
 * Provides navigation layout state to all components that need it.
 * Uses a single localStorage instance shared across all consumers.
 */
export const NavigationLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const config = getNavigationMigrationConfig();
	const [userPreference, setUserPreference] = useLocalStorage(
		STORAGE_KEY,
		null as NavigationLayout | null,
	);

	// Determine current layout:
	// - If toggle is visible and user has preference, use it
	// - Otherwise, use date-based default
	const currentLayout: NavigationLayout = React.useMemo(() => {
		// When toggle is visible, always respect user preference if set
		// Otherwise, use date-based default
		let result: NavigationLayout;
		if (config.showToggle) {
			result = userPreference !== null ? userPreference : config.defaultLayout;
		} else {
			// After month 3, toggle is hidden, always use default
			result = config.defaultLayout;
		}

		return result;
	}, [config.showToggle, config.defaultLayout, userPreference]);

	const setLayout = React.useCallback(
		(layout: NavigationLayout) => {
			setUserPreference(layout);
		},
		[setUserPreference],
	);

	const value: NavigationLayoutContextValue = React.useMemo(
		() => ({
			currentLayout,
			setLayout,
			showToggle: config.showToggle,
		}),
		[currentLayout, setLayout, config.showToggle],
	);

	return (
		<NavigationLayoutContext.Provider value={value}>
			{children}
		</NavigationLayoutContext.Provider>
	);
};

/**
 * Hook to use navigation layout context
 *
 * @throws Error if used outside NavigationLayoutProvider
 */
export function useNavigationLayoutContext(): NavigationLayoutContextValue {
	const context = React.useContext(NavigationLayoutContext);
	if (!context) {
		throw new Error('useNavigationLayoutContext must be used within NavigationLayoutProvider');
	}

	return context;
}

export default NavigationLayoutContext;
